import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Plus, Search, Edit, Trash, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { clientService } from '@/lib/supabase/clients';

// Função para gerar ID único
const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
  notes: string;
}

export default function Clients() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentClient, setCurrentClient] = useState<Partial<Client>>({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  // Carregar clientes do Supabase
  useEffect(() => {
    const loadClients = async () => {
      try {
        setIsLoading(true);
        const clientsData = await clientService.getClients();
        setClients(clientsData);
      } catch (error) {
        console.error('Error loading clients:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os clientes",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadClients();
  }, [toast]);

  // Filtrar clientes com base no termo de busca
  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Função para abrir o formulário para criar um novo cliente
  const handleNewClient = () => {
    setCurrentClient({
      name: '',
      email: '',
      phone: '',
      notes: ''
    });
    setIsEditing(false);
    setShowForm(true);
  };

  // Função para abrir o formulário para editar um cliente existente
  const handleEditClient = (client: Client) => {
    setCurrentClient(client);
    setIsEditing(true);
    setShowForm(true);
  };

  // Função para salvar cliente (criar ou atualizar)
  const handleSaveClient = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentClient.name?.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, informe o nome do cliente.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsLoading(true);
      
      const clientData = {
        name: currentClient.name.trim(),
        email: currentClient.email?.trim() || undefined,
        phone: currentClient.phone?.trim() || '',
        notes: currentClient.notes?.trim() || ''
      };

      let savedClient;
      
      if (isEditing && currentClient.id) {
        // Atualizar cliente existente
        savedClient = await clientService.updateClient(currentClient.id, clientData);
        
        // Atualizar a lista de clientes
        setClients(clients.map(c => c.id === savedClient.id ? savedClient : c));
        
        toast({
          title: "Cliente atualizado",
          description: "O cliente foi atualizado com sucesso."
        });
      } else {
        // Criar novo cliente
        savedClient = await clientService.createClient(clientData);
        
        // Adicionar à lista de clientes
        setClients([...clients, savedClient]);
        
        toast({
          title: "Cliente adicionado",
          description: "O cliente foi adicionado com sucesso."
        });
      }
      
      setShowForm(false);
      setCurrentClient({
        name: '',
        email: '',
        phone: '',
        notes: ''
      });
      
    } catch (error) {
      console.error('Error saving client:', error);
      toast({
        title: "Erro",
        description: `Não foi possível ${isEditing ? 'atualizar' : 'criar'} o cliente`,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Função para deletar cliente
  const handleDeleteClient = async (id: string) => {
    if (!window.confirm("Tem certeza que deseja excluir este cliente?")) {
      return;
    }

    try {
      setIsLoading(true);
      await clientService.deleteClient(id);
      setClients(clients.filter(client => client.id !== id));

      toast({
        title: "Sucesso",
        description: "Cliente excluído com sucesso",
      });
    } catch (error) {
      console.error('Error deleting client:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o cliente",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Gerenciamento de Clientes</h1>
          <Button 
            onClick={handleNewClient}
            className="bg-voah-teal hover:bg-voah-teal-dark"
            disabled={isLoading}
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Cliente
          </Button>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                type="search"
                placeholder="Buscar por nome ou email..."
                className="pl-8 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {showForm && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <form onSubmit={handleSaveClient}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">{isEditing ? "Editar Cliente" : "Novo Cliente"}</h3>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowForm(false)}
                    className="border border-red-500 text-red-500 hover:bg-red-50"
                    disabled={isLoading}
                  >
                    Cancelar
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nome *</label>
                    <Input 
                      placeholder="Nome do cliente" 
                      value={currentClient.name} 
                      onChange={(e) => setCurrentClient({...currentClient, name: e.target.value})}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email (opcional)</Label>
                    <Input
                      id="email"
                      value={currentClient.email}
                      onChange={(e) => setCurrentClient({ ...currentClient, email: e.target.value })}
                      placeholder="email@exemplo.com"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Telefone (opcional)</label>
                    <Input 
                      placeholder="Telefone do cliente" 
                      value={currentClient.phone} 
                      onChange={(e) => setCurrentClient({...currentClient, phone: e.target.value})}
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Observações (opcional)</label>
                    <Input 
                      placeholder="Observações sobre o cliente" 
                      value={currentClient.notes} 
                      onChange={(e) => setCurrentClient({...currentClient, notes: e.target.value})}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button 
                    type="submit"
                    className="bg-voah-teal hover:bg-voah-teal-dark"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      "Salvar Cliente"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto">
            {isLoading && clients.length === 0 ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-voah-teal" />
                <span className="ml-2 text-gray-600">Carregando clientes...</span>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Nome</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Telefone</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-gray-500">
                        {searchTerm ? "Nenhum cliente encontrado para esta busca." : "Nenhum cliente cadastrado."}
                      </td>
                    </tr>
                  ) : (
                    filteredClients.map((client) => (
                      <tr key={client.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-800">{client.name}</td>
                        <td className="py-3 px-4 text-gray-800">{client.email || '-'}</td>
                        <td className="py-3 px-4 text-gray-800">{client.phone}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              title="Editar"
                              onClick={() => handleEditClient(client)}
                              disabled={isLoading}
                            >
                              <Edit className="h-4 w-4 text-gray-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              title="Remover"
                              onClick={() => handleDeleteClient(client.id)}
                              disabled={isLoading}
                            >
                              <Trash className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
} 